import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { MapObject } from '@wehunt/ui'
import { listTeamCameraPins } from './api/teamCameraPinsApi'
import { dtoToCameraMapObject } from './mapDtoMappers'

/**
 * The team's trail cameras as map pins — the cameras shared with the team, plus
 * the signed-in user's own.
 *
 * A trail camera IS a camera pin as far as a hunter is concerned: the mobile app
 * draws one per located camera on the same map as the ordinary map pins. Web
 * only ever read the `mapPins` collection, and no client writes a camera there
 * — the cameras live in the Burrel database — so every camera pin a user could
 * see in the app was missing from the team map on web, and the team's camera
 * count read 0 no matter how many cameras the ground had.
 *
 * A failure resolves to no camera pins rather than failing the page: the rest of
 * the map (border, areas, pins, lines) is worth drawing without them, and the
 * cameras come from an external service that can be down on its own.
 */
/**
 * One shared empty list, so a query that has not answered yet returns the SAME
 * value every render. A fresh `[]` each time changes the identity of everything
 * built from it — and the team map view re-frames the whole ground whenever the
 * map object it is handed changes identity, which is what reset a reader's zoom
 * on every unrelated re-render.
 */
const NO_PINS: MapObject[] = []

export function useGroupCameraPins(groupId: string): MapObject[] {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'

  const { data } = useQuery({
    queryKey: ['clubhouse', 'teams', groupId, 'camera-pins'],
    enabled: isSignedIn && !!groupId,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      const dtos = await listTeamCameraPins(groupId, token)
      return dtos.map(dtoToCameraMapObject)
    },
  })

  return data ?? NO_PINS
}
